import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Modal,
  Typography,
} from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import axios from "axios";
import Cookies from "js-cookie";

import styles from "./PrivacyPolicyModal.module.scss";

type LegalDocumentPayload = {
  document?: unknown;
  content?: unknown;
  document_content?: unknown;
  html_content?: unknown;
  data?: LegalDocumentPayload;
  legal_document?: LegalDocumentPayload;
};

type SaasModalProps = {
  open: boolean;
  onClose: () => void;
  content?: string | LegalDocumentPayload | null;
  showAcceptButton?: boolean;
  userId?: string | number | null;
};

const resolveDocumentHtml = (
  value: SaasModalProps["content"],
): string => {
  if (typeof value === "string") {
    return value;
  }

  if (!value || typeof value !== "object") {
    return "";
  }

  const candidates = [
    value.document,
    value.content,
    value.document_content,
    value.html_content,
    value.data?.document,
    value.data?.content,
    value.data?.document_content,
    value.data?.html_content,
    value.legal_document?.document,
    value.legal_document?.content,
    value.legal_document?.document_content,
    value.legal_document?.html_content,
  ];

  return (
    candidates.find(
      (candidate): candidate is string =>
        typeof candidate === "string" && Boolean(candidate.trim()),
    ) ?? ""
  );
};

const containsVisibleText = (html: string): boolean => {
  if (!html.trim()) {
    return false;
  }

  if (typeof DOMParser !== "undefined") {
    const parsedDocument = new DOMParser().parseFromString(html, "text/html");
    return Boolean(parsedDocument.body.textContent?.replace(/\u00a0/g, " ").trim());
  }

  return Boolean(
    html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;|&#160;/gi, " ")
      .trim(),
  );
};

const getMedicubeUserId = (response: unknown): string | number | undefined => {
  if (Array.isArray(response)) {
    const user = response.find(
      (item) => item && typeof item === "object" && "medicube_user_id" in item,
    ) as { medicube_user_id?: string | number } | undefined;

    return user?.medicube_user_id;
  }

  if (response && typeof response === "object") {
    const user = response as {
      medicube_user_id?: string | number;
      data?: { medicube_user_id?: string | number };
    };

    return user.medicube_user_id ?? user.data?.medicube_user_id;
  }

  return undefined;
};

const SaasModal: React.FC<SaasModalProps> = ({
  open,
  onClose,
  content,
  showAcceptButton = false,
  userId,
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeclineConfirmation, setShowDeclineConfirmation] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [documentHtml, setDocumentHtml] = useState("");
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState("");

  const providedDocumentHtml = useMemo(
    () => resolveDocumentHtml(content),
    [content],
  );
  const hasVisibleContent = useMemo(
    () => containsVisibleText(documentHtml),
    [documentHtml],
  );

  useEffect(() => {
    if (!open) {
      setIsChecked(false);
      setIsSubmitting(false);
      setShowDeclineConfirmation(false);
      setSubmitError("");
      setDocumentHtml("");
      setIsLoadingDocument(false);
      setDocumentError("");
      return;
    }

    const controller = new AbortController();
    const hasProvidedContent = containsVisibleText(providedDocumentHtml);

    setDocumentHtml(providedDocumentHtml);
    setDocumentError("");
    setIsLoadingDocument(!hasProvidedContent);

    const loadSaasAgreement = async () => {
      try {
        const { data } = await axios.get("/document_agreement/2", {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });

        const apiDocumentHtml = resolveDocumentHtml(data);

        if (!containsVisibleText(apiDocumentHtml)) {
          console.warn(
            "The SaaS agreement API returned HTML without visible text.",
            {
              documentLength: apiDocumentHtml.length,
              response: data,
            },
          );

          if (!hasProvidedContent) {
            setDocumentHtml(apiDocumentHtml);
            setDocumentError(
              "The server returned the SaaS agreement record, but document ID 2 does not contain any visible text.",
            );
          }

          return;
        }

        setDocumentHtml(apiDocumentHtml);
        setDocumentError("");
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }

        console.error("Failed to load the SaaS agreement:", error);

        if (!hasProvidedContent) {
          setDocumentError(
            "We could not load the SaaS agreement. Please close the window and try again.",
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingDocument(false);
        }
      }
    };

    void loadSaasAgreement();

    return () => controller.abort();
  }, [open, providedDocumentHtml]);

  const handleAccept = async () => {
    if (!isChecked || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      let agreementUserId = userId;

      if (
        agreementUserId == null ||
        String(agreementUserId).trim() === ""
      ) {
        const { data } = await axios.get("/user");
        agreementUserId = getMedicubeUserId(data);
      }

      if (
        agreementUserId == null ||
        String(agreementUserId).trim() === ""
      ) {
        throw new Error("The current Medicube user ID could not be resolved.");
      }

      await axios.post(
        "/document_agreement",
        {
          legal_document_id: 2,
          user_type: "system_user",
          user_id: agreementUserId,
          patient_id: 0,
          consent_bundle_id: 0,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      onClose();
    } catch (error) {
      console.error("Failed to send document agreement:", error);
      setSubmitError(
        "We could not save your acceptance. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const logOut = () => {
    Cookies.remove("token");
    delete axios.defaults.headers.common.Authorization;
    window.location.replace("/login");
  };

  return (
    <>
      <Modal
        open={open}
        onClose={showAcceptButton ? undefined : onClose}
        disableEscapeKeyDown={showAcceptButton}
        className={styles.modal}
      >
        <Box
          className={styles.modalContent}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "80vh",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              paddingRight: 2,
            }}
          >
            {isLoadingDocument ? (
              <Box
                sx={{
                  minHeight: 240,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : documentError ? (
              <Alert severity="warning">
                <AlertTitle>SaaS agreement content is unavailable</AlertTitle>
                {documentError}
              </Alert>
            ) : hasVisibleContent ? (
              <div dangerouslySetInnerHTML={{ __html: documentHtml }} />
            ) : (
              <Alert severity="warning">
                <AlertTitle>SaaS agreement content is unavailable</AlertTitle>
                The server returned the agreement record, but its document field
                does not contain any visible text. Please add the complete SaaS
                agreement HTML to legal document ID 2.
              </Alert>
            )}
          </Box>

          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}

          {showAcceptButton ? (
            <Box
              sx={{
                borderTop: "1px solid #ddd",
                pt: 2,
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(event) => setIsChecked(event.target.checked)}
                  />
                }
                label="I agree to the Software as a Service Subscription Agreement"
              />

              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  onClick={handleAccept}
                  variant="contained"
                  color="primary"
                  disabled={
                    !isChecked ||
                    isSubmitting ||
                    isLoadingDocument ||
                    !hasVisibleContent
                  }
                >
                  {isSubmitting ? "Saving..." : "Accept"}
                </Button>

                <Button
                  onClick={() => setShowDeclineConfirmation(true)}
                  variant="outlined"
                  color="error"
                  disabled={isSubmitting}
                >
                  Decline
                </Button>
              </Box>
            </Box>
          ) : (
            <Button onClick={onClose} variant="contained" color="primary">
              Close
            </Button>
          )}
        </Box>
      </Modal>

      <Modal
        open={showDeclineConfirmation}
        onClose={() => setShowDeclineConfirmation(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            maxWidth: "calc(100vw - 32px)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <WarningAmber sx={{ fontSize: 40, color: "orange", mb: 2 }} />

          <Typography variant="h6">
            Are you sure you want to decline?
          </Typography>

          <Typography sx={{ mt: 2 }}>
            You will no longer be able to access Medicube services until you
            agree to the updated terms and conditions.
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
            <Button
              onClick={() => setShowDeclineConfirmation(false)}
              variant="contained"
              color="secondary"
            >
              Go back
            </Button>

            <Button onClick={logOut} variant="contained" color="error">
              Confirm &amp; Exit
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SaasModal;
